import { Injectable } from '@angular/core';
import {catchError, distinctUntilChanged, filter, first, map, switchMap, tap} from 'rxjs/operators';
import { GetAllTurbineByTypesDataStudioGQL, GetParksAndTurbinesDataStudioGQL, GetCsSignalsParkNameGQL, GetSignalsByParkNameGQL, GetSignalsByTurbineNameGQL, GetMetadataByParkNameGQL, GetSignalsByTurbineTypesGQL, AllTurbinesGQL, Stream, SignalsByTurbineDsGQL, FileListGQL, DeleteFileGQL, DataStudioFiles, FileListNewGQL, GetFileRequestStatusGQL, GetCsSignalsParkNameQuery, GetMetMastsSignalsParkNameGQL, GetMetMastsSignalsParkNameQuery, DeviceType } from '../graphql';
import { environment } from 'environments/environment';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {BehaviorSubject, combineLatest, forkJoin, Observable, of, Subject} from 'rxjs';
import {InitialDataService} from '../initial-data/initial-data.service';
import { ExcelExportService } from 'app/shared/services/export/excel/excel-export.service';
import { TranslateService } from '@ngx-translate/core';
import { TimezoneService } from '../../timezone/timezone.service';
import _ from 'lodash';
import { CapitalizeFirstPipe } from 'app/shared/pipes/capitalize-first/capitalize-first.pipe';
import { DimensionService } from 'app/analyze/service/dimension-service/dimension.service';
import { MetmastsDataWithSignals, QueryParams } from 'app/shared/models/data-studio.model';

import { DEVICE_TYPES } from 'app/data-studio-redesign/interfaces/data-studio.interface';
import { Apollo } from 'apollo-angular';
import { UserProfileService } from 'app/shared/user-profile/user-profile.service';

@Injectable({
  providedIn: 'root'
})
export class DataStudioRedesignService {

  public isDownloading$ = new BehaviorSubject(false);
  public isRequesting$ = new BehaviorSubject(false);
  public requestingSignalsData = false;

  private turbines: Subject<Array<any>>  = new Subject();
  private turbineObeservable = this.turbines.asObservable();
  private signals: Subject<Array<any>>  = new Subject();
  private signalsObservable = this.signals.asObservable();
  private timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  private signalsParkMap = {
    productionScada: 'SIGNALS.PRODUCTION_SCADA.DISPLAY_NAME',
    productionGrid: 'SIGNALS.PRODUCTION_GRID.DISPLAY_NAME',
    electricityLosses: 'SIGNALS.ELECTRICITY.DISPLAY_NAME',
    consumptionGrid: 'SIGNALS.ENERGY_CONSUMPTION.DISPLAY_NAME',
    electricitySalesRevenue: 'SIGNALS.REV_ELEC.DISPLAY_NAME',
    incentiveRevenue: 'SIGNALS.REV_INC.DISPLAY_NAME',
    totalRevenue: 'SIGNALS.TOTAL_REV.DISPLAY_NAME',
    consumptionLosses: 'SIGNALS.ENERGY_CONSUMP.DISPLAY_NAME',
    marketPrice: 'SIGNALS.AVG_MARKET_ELEC.DISPLAY_NAME',
    PPAElectricityPrice:'SIGNALS.AVG_PPA_ELEC.DISPLAY_NAME',
    temperature: 'SIGNALS.AVG_TEMPERATURE.DISPLAY_NAME',
    windSpeed: 'SIGNALS.AVG_WIND_SPEED.DISPLAY_NAME',
    theoreticalProduction: 'SIGNALS.THEORETICAL_PRODUCTION.DISPLAY_NAME'

};
  constructor(private getParkAndTurbinesGql: GetParksAndTurbinesDataStudioGQL,
              private getTurbineTypes: GetAllTurbineByTypesDataStudioGQL,
              private getSignalsTurbineType: GetSignalsByTurbineTypesGQL,
              private getSignalsParkName: GetSignalsByParkNameGQL,
              private getCSSignalsParkName: GetCsSignalsParkNameGQL,
              private getMetMastsSignalsParkName: GetMetMastsSignalsParkNameGQL,
              private getSignalsTurbineName: GetSignalsByTurbineNameGQL,
              private getMetadataByParkName: GetMetadataByParkNameGQL,
              private userProfileService: UserProfileService,
              private allTurbinesGQL: AllTurbinesGQL,
              private signalsByTurbineDS: SignalsByTurbineDsGQL,
              private timezoneService: TimezoneService,
              private fileList: FileListGQL,
              private fileListNew: FileListNewGQL,
              private fileRequestStatus: GetFileRequestStatusGQL,
              private deleteFile: DeleteFileGQL,
              private http: HttpClient,
              private excelService: ExcelExportService,
              private initialDataService: InitialDataService,
              private translateService: TranslateService,
              private capitalizeFirstPipe: CapitalizeFirstPipe,
              private dimensionService: DimensionService
            ) { }

  public getAllParkAndTurbines() {
    return this.getParkAndTurbinesGql.fetch({
      variables: {},
      fetchPolicy: 'network-only' 
    })
      .pipe(map((result: any) => {
        return result.data.parks;
      }));
  }

  sendTurbinesToPreview(turbines: Array<any>) {
    this.turbines.next(turbines);
  }

  getTurbines(): Observable<Array<any>> {
    return this.turbineObeservable;
  }

  sendSignalsToPreview(signals: Array<any>) {
    this.signals.next(signals);
  }
  getSignals() {
    return this.signalsObservable;
  }
  public getAllTurbineTypes() {
    return this.getTurbineTypes.fetch({
      variables: {},
      fetchPolicy: 'network-only' 
    })      
      .pipe(map((result: any) => {
        return result;
      }));
  }

  public getAllTurbines() {
    return this.allTurbinesGQL.fetch().pipe(map(data => data.data.turbines));
  }

  public getAvailableTS(parkname: string) {
    return this.initialDataService.getFirstAvailableDataAsTimestampByPark(parkname);
  }

  public getAllSignalsByTurbineType(turbineType, streamType) {
    return this.getSignalsTurbineType.fetch({ 
      variables: {
        turbineType, streamType 
      }, 
      fetchPolicy: 'network-only' })
      .pipe(map((result: any) => {
        return result.data.turbines;
      }));
  }

  public getAllSignalsByParkName(parkname, streamType) {
    return this.getSignalsParkName.fetch({ 
      variables: { parkname, streamType },  
      fetchPolicy: 'network-only' })
      .pipe(map((result: any) => {
        return result.data.parks;
      }));
  }

  public getCSSignalsByParkName(parkname) {
    return this.getCSSignalsParkName.fetch({ 
      variables: {parkname }, 
      fetchPolicy: 'network-only' })
      .pipe(map((result: Apollo.QueryResult<GetCsSignalsParkNameQuery>) => {
        return result.data.parks.flatMap(park => park.controlSystem
          .flatMap(cs => ({ ...cs, signalsList: cs.signalsList.map(signal => ({ ...signal, unit: signal.unit || ''}))})));
      }));
  }

  getMetMastsignalsByParkName(parkname): Observable<MetmastsDataWithSignals[]> {
    return this.getMetMastsSignalsParkName.fetch({ 
      variables: { parkname }, 
      fetchPolicy: 'network-only' })
      .pipe(map((result: Apollo.QueryResult<GetMetMastsSignalsParkNameQuery>) => {
        return result.data.parks.flatMap(park => park.metmasts
          .flatMap(({name, sensorsList, _id, code}) => ({
            name,
            _id,
            code,
            sensorsList: sensorsList.map(({_id, _id_metmast, name, signalsList}) => ({
              _id,
              _id_metmast,
              name,
              signalsList: signalsList.map(({dbName, displayName, unit}) => ({
                dbName,
                displayName,
                metmastId: _id_metmast,
                deviceId: _id,
                unit: unit || ''
            }))
          }))})));
      }));
  }

  public getAllMetadataByParkName(parkname) {
    return this.getMetadataByParkName.fetch({ 
      variables: { parkname }, 
      fetchPolicy: 'network-only' })
      .pipe(map((result: any) => {
        return result.data.parks;
      }));
  }


  public signalsByTurbine(turbineObservable: Observable<{_id: string, code: string, name: string, type: string, serialNav: number}>, stream: Stream) {
    return turbineObservable.pipe(
      distinctUntilChanged(),
      switchMap((turbine) => {
        return this.signalsByTurbineDS.fetch({ variables: {name: turbine.name, streamType: [stream]} })
        .pipe(map((result) => {
          return result.data.turbines[0].signalsList;
        }));
      })
    );
  }

  public getAllSignalsByTurbineName(turbinename, streamType) {
    return this.getSignalsTurbineName.fetch({
      variables: { turbinename, streamType },
      fetchPolicy: 'network-only' })
      .pipe(map((result: any) => {
        return result.data.turbines;
      }));
  }

  public getRequestFile(input: any) {
    input.translates = Object.fromEntries(input.signals.map(key => [key, input.queryType === "parkdata" ?
      this.translateService.instant(this.signalsParkMap[key]??key)  : input.queryType === "tlog" ? this.capitalizeFirstPipe.transform(key) :
      this.translateService.instant('SIGNALS.' + key + '.DISPLAY_NAME')]));
    const headers = { Authorization: 'Bearer ' + this.userProfileService.getToken() };
    const url = environment.dataStudioURL + `/api/v1/${input.queryType}/${input.fileType}`;
    this.isRequesting$.next(true);
    input.granularity = this.parseGranularityForGraphApi(input.granularity);
    input.endTS =  this.getQueryRangeEnd({ endTS: input.endTS, granularity: input.granularity });
    const oldOffset = input.timeZoneDiff;
    input.timeZone = this.timezoneService.selectedOption ?? this.timeZone;
    input.timeZoneDiff = this.timezoneService.getOffsetForTimezone(input.timeZone);
    if (input.queryType !== 'tlog' && input.queryType !== 'live') {
      input.startTS = _.isNil(oldOffset) ? this.timezoneService.applyTzToDate(input.startTS).valueOf() :  input.startTS - input.timeZoneDiff * 60* 60 * 1000 + oldOffset * 60 * 60 * 1000;
      input.endTS = _.isNil(oldOffset) ? this.timezoneService.applyTzToDate(input.endTS).valueOf() :  input.endTS - input.timeZoneDiff * 60* 60 * 1000 + oldOffset * 60 * 60 * 1000;
    }
    input.signals = input.signals.slice().sort((a, b) => {
      const valA = input.translates[a];
      const valB = input.translates[b];
      return valA.localeCompare(valB);
    });
    return this.http.post(url, input, { headers, reportProgress: true, observe: 'body', responseType: 'blob' });
  }

  public getRequestNew(input: any) {
    input.translates = Object.fromEntries(input.signals.map(key => [key, input.queryType === "parkdata" ?
      this.translateService.instant(this.signalsParkMap[key]) : input.queryType === "tlog" ? this.capitalizeFirstPipe.transform(key) :
      this.translateService.instant('SIGNALS.' + key + '.DISPLAY_NAME')]));
    const headers = { Authorization: 'Bearer ' + this.userProfileService.getToken() };
    const url = environment.dataStudioURL + `/api/v1/${input.queryType}/${input.fileType}`;
    this.isRequesting$.next(true);
    input.granularity = input.deviceType ? input.granularity : this.parseGranularityForGraphApi(input.granularity);
    input.endTS =  this.getQueryRangeEnd({ endTS: input.endTS, granularity: input.granularity });
    const oldOffset = input.timeZoneDiff;
    input.timeZone = this.timezoneService.selectedOption ?? this.timeZone;
    input.timeZoneDiff = this.timezoneService.getOffsetForTimezone(input.timeZone);
    if (input.queryType !== 'tlog' && input.queryType !== 'live') {
      input.startTS = _.isNil(oldOffset) ? this.timezoneService.applyTzToDate(input.startTS).valueOf() :  input.startTS - input.timeZoneDiff * 60* 60 * 1000 + oldOffset * 60 * 60 * 1000;
      input.endTS = _.isNil(oldOffset) ? this.timezoneService.applyTzToDate(input.endTS).valueOf() :  input.endTS - input.timeZoneDiff * 60* 60 * 1000 + oldOffset * 60 * 60 * 1000;
    }
    input.signals = input.signals.slice().sort((a, b) => {
      const valA = input.translates[a];
      const valB = input.translates[b];
      return valA.localeCompare(valB);
    });
    return this.http.post(url, {...input, translates: undefined}, { headers, reportProgress: true });
  }

  private filteredClone(obj: any, filter: string[], prefix:string):any {
    const newObj = Object.keys(obj).reduce((acc, key) => {
      if (filter.includes(key) || filter.includes(prefix + key)) {
        acc[key] = obj[key]?.name ? obj[key].name : obj[key];
      }
      return acc;
    }, {});
    return newObj;
  }

  public getMetadataFile(fileRequest:any, filename:string):any {
    let showParkTab = true;
    this.getAllMetadataByParkName(fileRequest.park).subscribe(parkSignals => {
      if (parkSignals) {
        const filteredParkSignals = this.filteredClone(parkSignals[0], fileRequest.signals, 'p-');
        if (Object.keys(filteredParkSignals).length === 0) {
          showParkTab = false;
        }
        filteredParkSignals.turbines = [];
        parkSignals[0].turbines.forEach(turbine => {
          if (fileRequest.turbines.includes(turbine?._id)) {
            filteredParkSignals.turbines.push(this.filteredClone(turbine, fileRequest.signals, 't-'));
          }
        });
          this.excelService.exportDataStudioMetadataAsFile(
            filteredParkSignals,
            filename,
            showParkTab
        );
        return parkSignals;
      }
    });
  }

  public getDownloadFile(input: any) {
    const headers = { Authorization: 'Bearer ' + this.userProfileService.getToken() };
    const url = environment.dataStudioURL + `/api/v1/${input.queryType}/${input.fileType}`;
    this.isDownloading$.next(true);
    input.granularity = this.parseGranularityForGraphApi(input.granularity);
    const oldOffset = input.timeZoneDiff ?? 0;
    input.timeZone = this.timezoneService.selectedOption ?? this.timeZone;
    input.timeZoneDiff = this.timezoneService.getOffsetForTimezone(input.timeZone);
    if (input.queryType !== 'tlog' && input.queryType !== 'live') {
      input.startTS = input.startTS + oldOffset * 60 * 60 * 1000 - input.timeZoneDiff * 60 * 60 * 1000 ;
      input.endTS = input.endTS + oldOffset * 60 * 60 * 1000 - input.timeZoneDiff * 60 * 60 * 1000 ;
    }

    return this.http.post(url, input, { headers, reportProgress: true, observe: 'body', responseType: 'blob' });
  }

  public getDownloadFileNew(url: any) {
    const headers = { Authorization: 'Bearer ' + this.userProfileService.getToken() };
    return this.http.get(url, { headers, reportProgress: true, observe: 'body', responseType: 'blob' });
  }


  public requestSingleTurbine(
    turbineObservable: Observable<{_id: string, code?: string, name?: string, type?: string, serialNav?: number}>,
    signalsObservable: Observable<string[]>, 
    queryParamsObservable: Observable<QueryParams>,
    refresh?: Observable<any>,
    customTimezone: string = null,
  ) {
      const headers = { Authorization: 'Bearer ' + this.userProfileService.getToken() };
      this.requestingSignalsData = true;
    // const url = 'https://data-studio-service-dev.wind-log.com/api/v1/live/graph';
    return combineLatest([turbineObservable, signalsObservable, queryParamsObservable, refresh]).pipe(
      filter(([turbine, signals, queryParam]) => !!signals && turbine !== null && queryParam !== null),
      switchMap(([turbine, signals, queryParam]) => {
        const url = environment.dataStudioURL + '/api/v1/' + queryParam.queryType + '/graph';
        const selectedTimezone = customTimezone !== null ? customTimezone : this.timezoneService.selectedOption;
        const timeZoneDiff =  this.timezoneService.getOffsetForTimezone(selectedTimezone);
        const isPark = queryParam.queryType === 'parkdata';
        const parsedGranularity = this.parseGranularityForGraphApi(queryParam.granularity)

        const endTS = this.getQueryRangeEnd({
          endTS: queryParam.endTS,
          granularity: parsedGranularity
        });

        const body = {
          [isPark ? 'park' : 'deviceIds']: isPark ? turbine.name : [turbine._id],
          deviceType: isPark ? undefined : DEVICE_TYPES.TURBINE,
          signals,
          granularity: parsedGranularity,
          queryType: queryParam.queryType,
          startTS:  this.applyCustomTzToDate(queryParam.startTS, queryParam.queryType, customTimezone),
          endTS:  this.applyCustomTzToDate(endTS, queryParam.queryType, customTimezone),
          fileType: 'graph',
          fileName: 'LiveXLSX',
          timeZoneDiff,
          timeZone: selectedTimezone ?? this.timeZone
        };
        if (!signals.length) return of([]);
        return this.http.post(url, body, { headers })
          .pipe(first())
          .pipe(catchError((err: HttpErrorResponse) => {
            return of([]);
          }));
      })
    );
  }

  public requestTurbineSignalsData(
    turbineIds: string[],
    signalsIds: string[],
    queryParams: QueryParams) {
    const headers = { Authorization: 'Bearer ' + this.userProfileService.getToken() };
    this.requestingSignalsData = true;

    const url = environment.dataStudioURL + '/api/v1/' + queryParams.queryType + '/graph';
    const timeZoneDiff =  this.timezoneService.getOffsetForTimezone(this.timezoneService.selectedOption);
    const parsedGranularity = this.parseGranularityForGraphApi(queryParams.granularity)

    const endTS = this.getQueryRangeEnd({
      endTS: queryParams.endTS,
      granularity: parsedGranularity
    });

    const body = {
      deviceIds: turbineIds,
      deviceType: DEVICE_TYPES.TURBINE,
      signals: signalsIds,
      granularity: parsedGranularity,
      queryType: queryParams.queryType,
      startTS:  this.applyTzToDate(queryParams.startTS, queryParams.queryType),
      endTS:  this.applyTzToDate(endTS, queryParams.queryType),
      fileType: 'graph',
      fileName: 'LiveXLSX',
      timeZoneDiff,
      timeZone: this.timezoneService.selectedOption ?? this.timeZone
    };
    if (!signalsIds.length || !turbineIds.length) return of([]);
    return this.http.post(url, body, { headers })
      .pipe(first())
      .pipe(catchError((err: HttpErrorResponse) => {
        return of(null);
      }));
  }

  public requestControlSystemData(
    csIds: string[],
    signals: string[],
    queryParams: QueryParams,
  ) {
    const headers = { Authorization: 'Bearer ' + this.userProfileService.getToken() };
    this.requestingSignalsData = true;
    // const url = 'https://data-studio-service-dev.wind-log.com/api/v1/live/graph';
    const url = `${environment.dataStudioURL}/api/v1/live/graph`;
    const timeZoneDiff =  this.timezoneService.getOffsetForTimezone(this.timezoneService.selectedOption);
    const parsedGranularity = this.parseGranularityForGraphApi(queryParams.granularity)

    const endTS = this.getQueryRangeEnd({
      endTS: queryParams.endTS,
      granularity: parsedGranularity
    });
    const body = {
      signals,
      granularity: 'live',
      queryType: 'live',
      startTS:  this.applyTzToDate(queryParams.startTS, queryParams.queryType),
      endTS:  this.applyTzToDate(endTS, queryParams.queryType),
      fileType: 'graph',
      fileName: 'LiveXLSX',
      timeZoneDiff,
      timeZone: this.timezoneService.selectedOption ?? this.timeZone,
      deviceType: DEVICE_TYPES.CONTROL_SYSTEM,
      deviceIds: csIds // [ID]
    };
    if (!signals.length || !csIds.length) return of([]);
    return this.http.post(url, body, { headers })
      .pipe(first())
      .pipe(catchError((err: HttpErrorResponse) => {
        return of(null);
    }));
  }

  public requestSingleControlSystem(controlSystems: Observable<string[]>, signals: Observable<string[]>, queryParam: Observable<QueryParams>, dataAnalysis: boolean = false, customTimezone: string = null) {
    const headers = { Authorization: 'Bearer ' + this.userProfileService.getToken() };
    return combineLatest([controlSystems, signals, queryParam]).pipe(
      filter(([controlSystems, signals, queryParam]) => {
        return !!controlSystems?.length && !!signals?.length && queryParam !== null
      }),
      switchMap(([controlSystems, signals, queryParam]) => {
        this.requestingSignalsData = true;
        const queryType = dataAnalysis ? queryParam.queryType : 'live';
        // const url = 'https://data-studio-service-dev.wind-log.com/api/v1/live/graph';
        const url = `${environment.dataStudioURL}/api/v1/${queryType}/graph`;
        const selectedTimezone = customTimezone !== null ? customTimezone : this.timezoneService.selectedOption;
        const timeZoneDiff =  this.timezoneService.getOffsetForTimezone(selectedTimezone);
        const parsedGranularity = this.parseGranularityForGraphApi(queryParam.granularity)

        const endTS = this.getQueryRangeEnd({
          endTS: queryParam.endTS,
          granularity: parsedGranularity
        });
        const body = {
          signals,
          granularity: dataAnalysis ? queryParam.granularity : 'live',
          queryType: queryType,
          startTS:  this.applyCustomTzToDate(queryParam.startTS, queryParam.queryType, customTimezone),
          endTS:  this.applyCustomTzToDate(endTS, queryParam.queryType, customTimezone),
          fileType: 'graph',
          fileName: 'LiveXLSX',
          timeZoneDiff,
          timeZone: selectedTimezone ?? this.timeZone,
          deviceType: DEVICE_TYPES.CONTROL_SYSTEM,
          deviceIds: controlSystems // [ID]
        };
        return this.http.post(url, body, { headers })
          .pipe(first())
          .pipe(catchError((err: HttpErrorResponse) => {
            return of([]);
        }));
      })
    );
  }

  public requestMetmast(
    sensorIds: string[],
    signals: string[],
    queryParams: QueryParams,
    dataAnalysis: boolean = false,
    customTimezone: string = null
  ) {
    const headers = { Authorization: 'Bearer ' + this.userProfileService.getToken() };
    this.requestingSignalsData = true;
    const queryType = dataAnalysis ? queryParams.queryType : 'live';
    // const url = 'https://data-studio-service-dev.wind-log.com/api/v1/live/graph';
    const url = `${environment.dataStudioURL}/api/v1/${queryType}/graph`;
    const selectedTimezone = customTimezone !== null ? customTimezone : this.timezoneService.selectedOption;
    const timeZoneDiff =  this.timezoneService.getOffsetForTimezone(selectedTimezone);
    const parsedGranularity = this.parseGranularityForGraphApi(queryParams.granularity)

    const endTS = this.getQueryRangeEnd({
      endTS: queryParams.endTS,
      granularity: parsedGranularity
    });

    const body = {
      signals,
      granularity: dataAnalysis ? queryParams.granularity : 'live',
      queryType: queryType,
      startTS:  this.applyCustomTzToDate(queryParams.startTS, queryParams.queryType, customTimezone),
      endTS:  this.applyCustomTzToDate(endTS, queryParams.queryType, customTimezone),
      fileType: 'graph',
      fileName: 'LiveXLSX',
      timeZoneDiff,
      timeZone: selectedTimezone ?? this.timeZone,
      deviceType: DEVICE_TYPES.METMAST,
      deviceIds: sensorIds// [ID]
    };
    if (!signals.length || !sensorIds.length) return of([]);
    return this.http.post(url, body, { headers })
      .pipe(first())
      .pipe(catchError((err: HttpErrorResponse) => {
        return of(null);
    }));
  }

   public requestMultipleTurbine(
    turbineObservable: Observable<{ _id: string, code: string, name: string, type: string, serialNav: number }[]>,
    signalObservable: Observable<string>,
    queryParamsObservable: Observable<{ granularity: string; queryType: string, startTS: number, endTS: number }>,
    refresh: Observable<any>
  ) {
    const headers = { Authorization: 'Bearer ' + this.userProfileService.getToken() };
    // const url = 'https://data-studio-service-dev.wind-log.com/api/v1/live/graph';
    this.requestingSignalsData = true;
    return combineLatest([turbineObservable, signalObservable, queryParamsObservable, refresh]).pipe(
      filter(([turbines, signal, queryParam]) => (signal !== null && queryParam !== null) && queryParam.queryType !== 'parkdata'),
      switchMap(([turbines, signal, queryParam]) => {
        const url = environment.dataStudioURL + '/api/v1/' + queryParam.queryType + '/graph';
        const body = {
          deviceIds: turbines.map(e => e._id),
          deviceType: DEVICE_TYPES.TURBINE,
          signals: [signal],
          granularity: this.parseGranularityForGraphApi(queryParam.granularity),
          queryType: queryParam.queryType,
          startTS: this.applyTzToDate(queryParam.startTS, queryParam.queryType),
          endTS: this.applyTzToDate(queryParam.endTS, queryParam.queryType),
          fileType: 'graph',
          fileName: 'LiveXLSX',
          timeZoneDiff : this.timezoneService.getOffsetForTimezone(this.timezoneService.selectedOption),
          timeZone:  this.timezoneService.selectedOption ?? this.timeZone
        };
        if (!turbines.length) return of([]);
        return this.http.post(url, body, { headers })
          .pipe(catchError((err: HttpErrorResponse) => {
            return of([]);
          }));
      })
    );
  }

  public requestMultipleTurbineParallel(
    deviceObservable: Observable<{ _id: string }[]>,
    signalObservable: Observable<string>,
    queryParamsObservable: Observable<QueryParams>,
    refresh: Observable<any>
  ) {
    const headers = { Authorization: 'Bearer ' + this.userProfileService.getToken() };
    // const url = 'https://data-studio-service-dev.wind-log.com/api/v1/live/graph';
    this.requestingSignalsData = true;
    return combineLatest([deviceObservable, signalObservable, queryParamsObservable, refresh]).pipe(
      filter(([devices, signal, queryParam]) => {
        return (!!devices && signal !== null && queryParam !== null) && queryParam.queryType !== 'parkdata'}),
      switchMap(([devices, signal, queryParam]) => {
        const url = environment.dataStudioURL + '/api/v1/' + queryParam.queryType + '/graph';
        const timeZoneDiff = this.timezoneService.getOffsetForTimezone(this.timezoneService.selectedOption);
        const requests = [];
        const granularity =  this.parseGranularityForGraphApi(queryParam.granularity);
        devices.forEach((t) => {
          const body = {
            deviceType: DEVICE_TYPES.TURBINE,
            deviceIds: [t._id],
            signals: [signal],
            granularity,
            queryType: queryParam.queryType,
            startTS: this.applyTzToDate(queryParam.startTS, queryParam.queryType),
            endTS: this.applyTzToDate(queryParam.endTS, queryParam.queryType),
            fileType: 'graph',
            fileName: 'LiveXLSX',
            timeZoneDiff,
            timeZone: this.timezoneService.selectedOption ?? this.timeZone
          };
          const request = this.http.post(url, body, { headers })
              .pipe(catchError((err: HttpErrorResponse) => {
                return of([]);
              }));

          requests.push(request);
        })

        if (!requests.length) return of([]);
        return forkJoin([...requests]).pipe(tap( res => {
          res.forEach((data:any[]) => {
            data.forEach(item => {
              item.Turbine = this.dimensionService.getTurbineCodeDisplay(item.Turbine);
              item.ControlSystem = item['Control System'];
              item?.forEach?.(signal => {
                signal.Turbine = this.dimensionService.getTurbineCodeDisplay(signal.Turbine);
                item.ControlSystem = item['Control System'];
              });
            });
          })
        }));
      })
    );
  }

  private applyTzToDate(date: number, quertType) {
    if(quertType === 'tlog' || quertType === 'live') return date;
    else return this.timezoneService.applyTzToDate(date).valueOf();
  }

  private applyCustomTzToDate(date: number, quertType, customTimezone: string = null) {
    if(quertType === 'tlog' || quertType === 'live') return date;
    else return this.timezoneService.applyTzToDate(date, customTimezone).valueOf();
  }

  parseGranularityForGraphApi(granularity) {
    switch (granularity) {
      case 'G30S':
      case '30s':
        return '30s';
      case 'G10M':
      case '10m':
        return '10m';
      case 'G1H':
      case '1h':
        return '1h';
      case 'G1D':
      case '1d':
        return '1d';
      case 'G1MON':
      case '30d':
        return '30d';
      case 'G1Y':
      case '1y':
        return '1y';
    }
  }

  private getQueryRangeEnd({ endTS, granularity }) {
    switch (granularity) {
      case '30s':
      case '10m':
      case '1h':
        return endTS+1;
    }
    return endTS;
  }

  public getFileListNew(): Observable<any[]>  {
    return this.fileListNew.subscribe({fetchPolicy: 'no-cache'}).pipe(map( results => results?.data?.user?.dataStudioUploadedFiles));
  }

  public getFileList(): Observable<DataStudioFiles[]>  {
    return this.fileList.subscribe({fetchPolicy: 'no-cache'}).pipe(map( results => results?.data?.user?.dataStudioFiles));
  }


  public getFileRequestStatus(trackerId: string): Observable<any> {
    return this.fileRequestStatus.watch({
        variables: {trackerId}, 
        pollInterval: 5 * 1000
      })
      .valueChanges
      .pipe(
        filter(a => !a.loading && !!a.data),
        map(a => ({
          status: a.data.dataStudioUploadedFileStatus.status,
          link: a.data.dataStudioUploadedFileStatus.link,
          fileName: a.data.dataStudioUploadedFileStatus.fileName
      })))
  }

  public deleteTsFile(fileLink: string): Observable<boolean> {
    return this.deleteFile.mutate({variables: {fileLink}}).pipe(map(results => results.data.deleteDataStudioFile));
  }

}
